import { get } from 'lodash';
import React, { useRef } from 'react';
import { NetworkStatus } from '@apollo/client';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { handleFetchMore } from '@src/util/handleFetchMore';
import {
  NotificationGroupsDocument,
  useMarkAllNotificationsAsReadMutation,
  useNotificationGroupsQuery,
} from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import RefetchButton from './RefetchButton';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import Feed from './Feed';
import './styles.scss';

const Notifications = () => {
  const notificationCenterWrapperRef = useRef(null);

  const [markAllAsReadMutation] = useMarkAllNotificationsAsReadMutation();
  const markAllAsRead = () => {
    markAllAsReadMutation({
      optimisticResponse: {
        markAllNotificationsAsRead: true,
      },
      update: cache => {
        try {
          const { notificationGroups } = cache.readQuery({ query: NotificationGroupsDocument });
          const { result, pagination } = notificationGroups;

          cache.writeQuery({
            data: {
              notificationGroups: {
                ...notificationGroups,
                pagination: pagination,
                result: result.map(({ __typename, notifications }) => ({
                  __typename: __typename,
                  notifications: notifications.map(notification => ({
                    ...notification,
                    readAt: notification.readAt || new Date().toISOString(),
                  })),
                })),
              },
            },
            query: NotificationGroupsDocument,
          });
        } catch (error) {}
      },
    });

    trackEvent({
      context: MetricContext.NOTIFICATION_CENTER,
      interaction: MetricAction.MARK_ALL_AS_READ,
    });
  };

  const { data, fetchMore, networkStatus } = useNotificationGroupsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      paginationOptions: {
        limit: 20,
      },
    },
  });

  const pagination = get(data, 'notificationGroups.pagination', {});
  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading = [NetworkStatus.setVariables, NetworkStatus.loading].includes(networkStatus);
  const notificationGroups = get(data, 'notificationGroups.result', []);
  const isEmpty = !notificationGroups.length;

  function loadNewerNotificationGroups() {
    if (!isInitialLoading && !isFetchMoreLoading) {
      handleFetchMore(fetchMore, {
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }

          const prevNotifications = prev.notificationGroups;

          const {
            notificationGroups: { result, pagination },
          } = fetchMoreResult;

          return {
            notificationGroups: {
              ...prevNotifications,
              pagination: {
                ...prevNotifications.pagination,
                minCursor: pagination.minCursor,
              },
              result: [...result, ...prevNotifications.result],
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: 15,
            maxCursor: pagination.minCursor,
          },
        },
      });
    }
  }

  function loadOlderNotificationGroups() {
    const { scrollHeight, scrollTop } = notificationCenterWrapperRef.current;

    if (!isFetchMoreLoading && pagination.hasMoreResults && scrollHeight - scrollTop > window.innerHeight - 150) {
      handleFetchMore(fetchMore, {
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }

          const prevNotifications = prev.notificationGroups;

          const {
            notificationGroups: { result, pagination },
          } = fetchMoreResult;

          return {
            notificationGroups: {
              ...prevNotifications,
              pagination: {
                ...prevNotifications.pagination,
                hasMoreResults: pagination.hasMoreResults,
                maxCursor: pagination.maxCursor,
              },
              result: [...prevNotifications.result, ...result],
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: 10,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }

  return (
    <div
      className="notification-center-wrapper"
      onScroll={loadOlderNotificationGroups}
      ref={notificationCenterWrapperRef}
    >
      <RefetchButton fetchNewNotifications={() => loadNewerNotificationGroups()} />

      <div className="notification-center-wrapper__header-container">
        <div className="notification-center-wrapper__header-container-content">
          <div className="notification-center-wrapper__header-container-title">Notifications</div>
          {!isEmpty && (
            <div className="notification-center-wrapper__header-container-button" onClick={() => markAllAsRead()}>
              Mark all as read
            </div>
          )}
        </div>
      </div>

      {isInitialLoading ? (
        <LoadingState />
      ) : isEmpty ? (
        <EmptyState />
      ) : (
        <Feed notificationGroups={notificationGroups} />
      )}
    </div>
  );
};

export default Notifications;
